import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Database,
  Download,
  FileArchive,
  FileDown,
  FileText,
  RefreshCw,
  Search,
  SkipForward,
  Sparkles,
  User as UserIcon,
  XCircle,
} from 'lucide-react';
import {
  downloadAllImportFiles,
  downloadImportFile,
  getImportSession,
  listImportRecords,
  type ImportRecordRow,
  type ImportSessionDetail,
} from '@/api/import-history';
import {
  decodePayload,
  ENTITY_LABELS,
  formatBytes,
  formatDateTime,
  LOGICAL_FILE_LABELS,
  RECORD_STATUS_META,
  SESSION_STATUS_META,
  STEP_LABELS,
  STEP_ORDER,
} from '@/lib/import-history-labels';

const PAGE_SIZE = 50;

/**
 * Détail d'un import de l'assistant : résumé (qui/quand/volumes), fichiers
 * sources re-téléchargeables et trace de CHAQUE élément importé. Deux
 * lectures : vue lisible (JSON décodé en libellés FR) et vue technique
 * (uuid V2 + payload/résultat JSON bruts), via un interrupteur.
 */
export function ImportSessionDetailPage() {
  const { code = '', sessionId = '' } = useParams<{ code: string; sessionId: string }>();
  const navigate = useNavigate();
  const id = Number(sessionId);

  const [session, setSession] = useState<ImportSessionDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [technical, setTechnical] = useState(false);

  // Filtres + pagination des éléments (un seul état : un changement de
  // filtre revient toujours page 1).
  const [filters, setFilters] = useState({ step: 'all', status: 'all', q: '', page: 1 });
  const [query, setQuery] = useState('');
  const [records, setRecords] = useState<ImportRecordRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [recordsLoading, setRecordsLoading] = useState(false);
  // Garde anti-course : seule la dernière requête lancée écrit l'état.
  const fetchSeqRef = useRef(0);

  const stepFilter = filters.step;
  const setStepFilter = (step: string) => setFilters((f) => ({ ...f, step, page: 1 }));
  const setStatusFilter = (status: string) => setFilters((f) => ({ ...f, status, page: 1 }));
  const setPage = (updater: (p: number) => number) =>
    setFilters((f) => ({ ...f, page: updater(f.page) }));

  useEffect(() => {
    getImportSession(code, id)
      .then(setSession)
      .catch(() => setLoadError('Import introuvable pour cette école.'));
  }, [code, id]);

  // Recherche debouncée → filtre `q` (+ retour page 1).
  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters((f) => (f.q === query.trim() ? f : { ...f, q: query.trim(), page: 1 }));
    }, 350);
    return () => clearTimeout(handle);
  }, [query]);

  const fetchRecords = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    setRecordsLoading(true);
    try {
      const result = await listImportRecords(code, id, {
        step: filters.step === 'all' ? undefined : filters.step,
        status: filters.status === 'all' ? undefined : filters.status,
        q: filters.q || undefined,
        page: filters.page,
        limit: PAGE_SIZE,
      });
      if (seq !== fetchSeqRef.current) return;
      setRecords(result.items);
      setTotal(result.total);
    } catch {
      if (seq === fetchSeqRef.current) setRecords([]);
    } finally {
      if (seq === fetchSeqRef.current) setRecordsLoading(false);
    }
  }, [code, id, filters]);

  useEffect(() => {
    void fetchRecords();
  }, [fetchRecords]);

  // Étapes réellement présentes dans cet import (ordre d'exécution).
  const presentSteps = useMemo(() => {
    const steps = new Set([
      ...Object.keys(session?.recordCounts ?? {}),
      ...Object.keys(session?.stepsSummary ?? {}),
    ]);
    return STEP_ORDER.filter((s) => steps.has(s)).concat(
      Array.from(steps).filter((s) => !STEP_ORDER.includes(s)),
    );
  }, [session]);

  const stepMessages = useMemo(
    () =>
      presentSteps
        .map((stepId) => ({
          stepId,
          errors: session?.stepsSummary?.[stepId]?.errors ?? [],
          warnings: session?.stepsSummary?.[stepId]?.warnings ?? [],
        }))
        .filter((s) => s.errors.length > 0 || s.warnings.length > 0),
    [presentSteps, session],
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (loadError) {
    return (
      <div className="mx-auto max-w-5xl space-y-6">
        <Button variant="ghost" onClick={() => navigate(`/schools/${code}`)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour à la fiche
        </Button>
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{loadError}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const statusMeta = session ? (SESSION_STATUS_META[session.status] ?? SESSION_STATUS_META.running) : null;

  return (
    <div className="mx-auto max-w-5xl space-y-6 pb-16">
      {/* En-tête */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 mb-1 text-muted-foreground"
            onClick={() => navigate(`/schools/${code}`)}
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            École <span className="ml-1 font-mono">{code}</span>
          </Button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">
              {session ? `Import du ${formatDateTime(session.startedAt)}` : 'Import'}
            </h1>
            {statusMeta && (
              <Badge variant="outline" className={`border-0 ${statusMeta.className}`}>
                {statusMeta.label}
              </Badge>
            )}
          </div>
          {session && (
            <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
              <UserIcon className="h-3.5 w-3.5" />
              Réalisé par <span className="font-medium">{session.createdBy || 'Inconnu'}</span>
              {session.targetSchoolPeriodLabel && <span>· {session.targetSchoolPeriodLabel}</span>}
            </p>
          )}
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
          <Switch checked={technical} onCheckedChange={setTechnical} />
          Vue technique
        </label>
      </div>

      {!session && !loadError && <Skeleton className="h-40 w-full" />}

      {session && (
        <>
          {/* Compteurs globaux */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <CounterCard
              label="Données traitées"
              value={session.totalItems}
              icon={<Database className="h-4 w-4 text-muted-foreground" />}
            />
            <CounterCard
              label="Créées"
              value={session.createdCount}
              icon={<CheckCircle2 className="h-4 w-4 text-green-600" />}
            />
            <CounterCard
              label="Mises à jour"
              value={session.updatedCount}
              icon={<RefreshCw className="h-4 w-4 text-blue-600" />}
            />
            <CounterCard
              label="Ignorées"
              value={session.skippedCount}
              icon={<SkipForward className="h-4 w-4 text-muted-foreground" />}
            />
            <CounterCard
              label="En échec"
              value={session.failedCount}
              icon={<XCircle className="h-4 w-4 text-red-600" />}
            />
          </div>

          {/* Rapport rédigé (IA) : synthèse + accès au rapport complet / PDF */}
          <Card>
            <CardHeader
              icon={<Sparkles />}
              iconColor="purple"
              actions={
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/schools/${code}/imports/${id}/report`)}
                  >
                    {session.aiAnalysis ? 'Voir le rapport complet' : 'Générer le rapport'}
                  </Button>
                  {session.aiAnalysis && (
                    <Button
                      size="sm"
                      onClick={() =>
                        window.open(`/schools/${code}/imports/${id}/report?print=1`, '_blank')
                      }
                    >
                      <FileDown className="mr-1 h-4 w-4" />
                      Télécharger en PDF
                    </Button>
                  )}
                </div>
              }
            >
              <CardTitle>Rapport de l'import</CardTitle>
            </CardHeader>
            <CardContent>
              {session.aiAnalysis ? (
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {session.aiAnalysis.executiveSummary}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Le rapport rédigé de cet import n'a pas encore été généré — il analyse les
                  chiffres exacts de l'import et met en avant ce qui doit être corrigé.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Répartition par étape */}
          {presentSteps.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Contenu de l'import</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {presentSteps.map((stepId) => {
                    const summary = session.stepsSummary?.[stepId];
                    const counts = session.recordCounts?.[stepId] ?? {};
                    const totalStep =
                      summary?.total ??
                      Object.values(counts).reduce((acc, n) => acc + n, 0);
                    const failed = summary?.failed ?? counts.failed ?? 0;
                    return (
                      <button
                        key={stepId}
                        type="button"
                        onClick={() => setStepFilter(stepFilter === stepId ? 'all' : stepId)}
                        className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                          stepFilter === stepId
                            ? 'border-primary bg-primary/10 font-medium'
                            : 'hover:bg-muted/60'
                        }`}
                      >
                        {STEP_LABELS[stepId] ?? stepId}
                        <span className="ml-1.5 text-muted-foreground">
                          {totalStep.toLocaleString('fr-FR')}
                        </span>
                        {failed > 0 && (
                          <span className="ml-1.5 text-red-600">{failed} échec{failed > 1 ? 's' : ''}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Fichiers sources */}
          <Card>
            <CardHeader
              icon={<FileArchive />}
              iconColor="teal"
              actions={
                session.files.length > 0 ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void downloadAllImportFiles(code, id)}
                  >
                    <Download className="mr-1 h-4 w-4" />
                    Tout télécharger (ZIP)
                  </Button>
                ) : undefined
              }
            >
              <CardTitle>Fichiers sources</CardTitle>
            </CardHeader>
            <CardContent>
              {session.files.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Aucun fichier source conservé pour cet import.
                </p>
              ) : (
                <div className="divide-y divide-border/60">
                  {session.files.map((file) => (
                    <div key={file.id} className="flex items-center gap-3 py-2.5">
                      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {LOGICAL_FILE_LABELS[file.logicalName] ?? file.logicalName}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {file.filename} · {formatBytes(file.sizeBytes)}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void downloadImportFile(code, id, file)}
                      >
                        <Download className="mr-1 h-4 w-4" />
                        Télécharger
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Erreurs / avertissements par étape */}
          {stepMessages.length > 0 && (
            <Card>
              <CardHeader icon={<AlertTriangle />} iconColor="orange">
                <CardTitle>Avertissements et erreurs</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {stepMessages.map(({ stepId, errors, warnings }) => (
                  <div key={stepId}>
                    <p className="mb-1 text-sm font-medium">{STEP_LABELS[stepId] ?? stepId}</p>
                    <ul className="space-y-1 text-sm">
                      {errors.map((message, i) => (
                        <li key={`e-${i}`} className="flex gap-2 text-red-700">
                          <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          <span>{message}</span>
                        </li>
                      ))}
                      {warnings.map((message, i) => (
                        <li key={`w-${i}`} className="flex gap-2 text-orange-700">
                          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          <span>{message}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Éléments importés */}
          <Card>
            <CardHeader icon={<Database />} iconColor="blue">
              <CardTitle>Éléments importés</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative min-w-52 flex-1">
                  <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    className="pl-8"
                    placeholder="Rechercher un élément (nom, email…)"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>
                <Select value={stepFilter} onValueChange={setStepFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les étapes</SelectItem>
                    {presentSteps.map((stepId) => (
                      <SelectItem key={stepId} value={stepId}>
                        {STEP_LABELS[stepId] ?? stepId}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filters.status} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-44">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="created">Créés</SelectItem>
                    <SelectItem value="updated">Mis à jour</SelectItem>
                    <SelectItem value="skipped">Ignorés</SelectItem>
                    <SelectItem value="failed">Échecs</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {recordsLoading && records === null && <Skeleton className="h-32 w-full" />}
              {records !== null && records.length === 0 && !recordsLoading && (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  Aucun élément ne correspond à ces critères.
                </p>
              )}

              {records !== null && records.length > 0 && (
                <div className={`divide-y divide-border/60 ${recordsLoading ? 'opacity-60' : ''}`}>
                  {records.map((record) => (
                    <RecordRow key={record.id} record={record} technical={technical} />
                  ))}
                </div>
              )}

              {total > PAGE_SIZE && (
                <div className="flex items-center justify-between pt-2">
                  <p className="text-sm text-muted-foreground">
                    {total.toLocaleString('fr-FR')} éléments — page {filters.page} / {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={filters.page <= 1 || recordsLoading}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      Précédent
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={filters.page >= totalPages || recordsLoading}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Suivant
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function CounterCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 py-4">
        {icon}
        <div>
          <p className="text-lg font-semibold leading-tight">{value.toLocaleString('fr-FR')}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Un élément importé. Vue lisible : libellés FR décodés du payload ;
 * vue technique : uuid V2 + JSON bruts (payload poussé et rapport V2).
 */
function RecordRow({ record, technical }: { record: ImportRecordRow; technical: boolean }) {
  const statusMeta = RECORD_STATUS_META[record.status] ?? RECORD_STATUS_META.created;
  const decoded = useMemo(() => decodePayload(record.payload), [record.payload]);

  return (
    <div className="space-y-1.5 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{record.label}</span>
        <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
          {ENTITY_LABELS[record.entityType] ?? record.entityType}
        </Badge>
        <Badge variant="outline" className={`border-0 text-xs ${statusMeta.className}`}>
          {statusMeta.label}
        </Badge>
      </div>

      {!technical && decoded.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {decoded.map((pair, i) => (
            <span key={pair.label}>
              {i > 0 && <span className="mx-1.5 opacity-50">·</span>}
              {pair.label}&nbsp;: <span className="text-foreground/80">{pair.value}</span>
            </span>
          ))}
        </p>
      )}

      {record.errorMessage && (
        <p
          className={`text-xs ${
            record.status === 'failed' ? 'text-red-600' : 'text-muted-foreground'
          }`}
        >
          {record.errorMessage}
        </p>
      )}

      {technical && (
        <div className="space-y-1.5">
          {record.v2Uuid && (
            <p className="font-mono text-xs text-muted-foreground">
              uuid V2&nbsp;: <span className="text-foreground/80">{record.v2Uuid}</span>
            </p>
          )}
          {record.payload && (
            <pre className="overflow-x-auto rounded-md bg-muted/60 p-2 font-mono text-xs">
              {JSON.stringify(record.payload, null, 2)}
            </pre>
          )}
          {record.result && (
            <pre className="overflow-x-auto rounded-md bg-muted/40 p-2 font-mono text-xs text-muted-foreground">
              {JSON.stringify(record.result, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
